import { useEffect, useState } from 'react'
import { AlertCircle, BellRing } from 'lucide-react'

import { DashboardLayout } from '@/components/layouts/DashboardLayout'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/lib/auth-context'
import { PERMISSIONS } from '@/lib/permissions'
import { cn } from '@/lib/utils'
import { showErrorToast } from '@/lib/toast'
import {
  ALERT_FAMILY_LABELS,
  getAlertSettings,
  groupAlertsByFamily,
  setAlertEnabled,
  type AlertSettingDto,
} from '@/lib/api/v2/alert-settings'

function AlertSwitch({
  checked,
  busy,
  onToggle,
  testId,
  label,
}: {
  checked: boolean
  busy: boolean
  onToggle: () => void
  testId: string
  label: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={busy}
      onClick={onToggle}
      data-testid={testId}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        checked ? 'bg-primary' : 'bg-muted',
        busy && 'opacity-50 cursor-not-allowed',
      )}
    >
      <span
        className={cn(
          'inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform',
          checked ? 'translate-x-5' : 'translate-x-0.5',
        )}
      />
    </button>
  )
}

export default function AlertSettingsPage() {
  const { user } = useAuth()
  const [alerts, setAlerts] = useState<AlertSettingDto[]>([])
  const [loading, setLoading] = useState(true)
  // Per-alert in-flight guard: toggling B must not clear A's guard (out-of-order PUTs would desync).
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set())

  const canManage = user?.permissions?.includes(PERMISSIONS.SETTINGS_MANAGE) === true

  useEffect(() => {
    if (!canManage) return
    let cancelled = false
    const load = async () => {
      try {
        const data = await getAlertSettings()
        if (!cancelled) setAlerts(data)
      } catch {
        if (!cancelled) showErrorToast('Erreur lors du chargement des alertes.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [canManage])

  const toggle = async (alert: AlertSettingDto) => {
    if (pending.has(alert.key)) return // ignore re-clicks while this alert's PUT is in flight
    const next = !alert.enabled
    setPending((prev) => new Set(prev).add(alert.key))
    // Optimistic: flip locally, roll back on failure.
    setAlerts((prev) => prev.map((a) => (a.key === alert.key ? { ...a, enabled: next, isDefault: false } : a)))
    try {
      await setAlertEnabled(alert.key, next)
    } catch {
      setAlerts((prev) => prev.map((a) => (a.key === alert.key ? { ...a, enabled: alert.enabled, isDefault: alert.isDefault } : a)))
      showErrorToast('Impossible de mettre à jour cette alerte.')
    } finally {
      setPending((prev) => {
        const nextSet = new Set(prev)
        nextSet.delete(alert.key)
        return nextSet
      })
    }
  }

  if (!canManage) {
    return (
      <DashboardLayout>
        <div className="max-w-xl mx-auto py-10 px-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Accès refusé</AlertTitle>
            <AlertDescription>Vous n'avez pas les droits pour gérer les alertes.</AlertDescription>
          </Alert>
        </div>
      </DashboardLayout>
    )
  }

  const groups = groupAlertsByFamily(alerts)

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <BellRing className="h-5 w-5 text-primary" />
            Alertes du suivi
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Activez ou désactivez chaque alerte pour votre organisation. Une alerte désactivée n'apparaît plus
            dans le Suivi, le cockpit et les métriques. Par défaut, toutes les alertes sont actives.
          </p>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-40 w-full rounded-xl" />
            ))}
          </div>
        ) : (
          groups.map((group) => (
            <Card key={group.family} className="py-4 gap-3">
              <CardHeader className="pb-0">
                <CardTitle className="text-base">{ALERT_FAMILY_LABELS[group.family]}</CardTitle>
              </CardHeader>
              <CardContent className="divide-y divide-border p-0">
                {group.alerts.map((alert) => (
                  <div key={alert.key} className="flex items-center justify-between gap-4 px-6 py-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={cn('text-sm font-medium', !alert.enabled && 'text-muted-foreground')}>
                          {alert.title}
                        </span>
                        <Badge variant={alert.severity === 'Hard' ? 'destructive' : 'secondary'} className="shrink-0">
                          {alert.severity === 'Hard' ? 'Bloquante' : 'Souple'}
                        </Badge>
                      </div>
                      {!alert.enabled && <span className="text-xs text-muted-foreground">Désactivée</span>}
                    </div>
                    <AlertSwitch
                      checked={alert.enabled}
                      busy={pending.has(alert.key)}
                      onToggle={() => void toggle(alert)}
                      testId={`alert-setting-toggle-${alert.key}`}
                      label={`Alerte ${alert.title}`}
                    />
                  </div>
                ))}
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </DashboardLayout>
  )
}
